import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { VERSION_FIELD_DESCRIPTOR } from './_versionField.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { INVOICE, InvoiceStatusValues, INVOICE_STATUS_LABELS, } from '../types/invoice.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for the fiscal invoice header (org/{orgId}/invoice).
 *
 * No lifecycle fields (transactional); business `status` only. Auditable.
 * Totals/itemCount/productsAmount computed in invoiceService; fiscal amounts
 * are denormalized snapshots.
 */
export const invoiceDescriptor = {
    name: INVOICE,
    label: 'Nota fiscal',
    domain: 'fiscal',
    collectionPath: 'org/{orgId}/invoice',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Fiscal invoice header (IN/OUT) with status machine and denormalized amounts',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        // NoCache: entidade versionada (spec 0045) — getById em cache devolve versão velha e anula a detecção de conflito.
        cache: CacheStrategy.NoCache,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true, description: 'Unique invoice identifier' },
        {
            name: 'number',
            example: '12345',
            label: 'Número',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.CODE,
            description: 'NF number (unique per org with series + direction)',
        },
        {
            name: 'series',
            example: '1',
            label: 'Série',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        {
            name: 'accessKey',
            example: '42261012345678000195550010000123451000123456',
            label: 'Chave de acesso',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NFE_ACCESS_KEY,
            description: 'NFe access key (44 digits)',
        },
        {
            name: 'protocol',
            example: '135240001234567',
            label: 'Protocolo',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        {
            name: 'legacyType',
            label: 'Tipo legado',
            type: 'number',
            required: false,
            description: 'Opaque ERP TIPO_NOTA',
        },
        {
            name: 'direction',
            example: 'OUT',
            label: 'Direção',
            type: 'string',
            required: true,
            description: 'IN (entrada) or OUT (saída)',
            defaultValue: 'OUT',
        },
        {
            name: 'issuerKind',
            example: 'OWN',
            label: 'Emitente',
            type: 'string',
            required: false,
            description: 'OWN or THIRD_PARTY',
        },
        {
            name: 'partyId',
            label: 'Parte',
            type: 'string',
            required: true,
            description: 'Customer (OUT) or supplier (IN) party',
        },
        { name: 'issueDate', example: '2026-10-20', label: 'Data de emissão', type: 'date', required: false },
        { name: 'entryDate', example: '2026-10-20', label: 'Data de entrada', type: 'date', required: false },
        {
            name: 'cfop',
            example: '5101',
            label: 'CFOP',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        {
            name: 'cfopComplement',
            example: 'Venda de produção do estabelecimento',
            label: 'Complemento CFOP',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NAME,
        },
        {
            name: 'state',
            example: 'SC',
            label: 'UF',
            type: 'string',
            required: false,
            maxLength: 2,
        },
        {
            name: 'freightType',
            example: 'CIF',
            label: 'Tipo frete',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        {
            name: 'paymentTerm',
            example: '28 dias',
            label: 'Prazo',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        {
            name: 'paymentMethod',
            example: 'Boleto',
            label: 'Forma de pagamento',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        {
            name: 'carrierId',
            label: 'Transportadora',
            type: 'string',
            required: false,
        },
        {
            name: 'salespersonId',
            label: 'Vendedor',
            type: 'string',
            required: false,
        },
        {
            name: 'customerOrderRef',
            example: '5531',
            label: 'Pedido do cliente',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        {
            name: 'notes',
            example: 'Entregar no portão 2',
            label: 'Observações',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NOTES,
        },
        {
            name: 'productsAmount',
            example: 9725,
            label: 'Valor produtos',
            type: 'number',
            required: false,
            description: 'Computed Σ item.totalPrice',
        },
        { name: 'discountAmount', example: 0, label: 'Desconto', type: 'number', required: false },
        { name: 'freightAmount', example: 0, label: 'Frete', type: 'number', required: false },
        { name: 'insuranceAmount', example: 0, label: 'Seguro', type: 'number', required: false },
        { name: 'otherExpensesAmount', example: 0, label: 'Outras despesas', type: 'number', required: false },
        { name: 'icmsAmount', example: 1167, label: 'ICMS', type: 'number', required: false },
        { name: 'icmsStAmount', example: 0, label: 'ICMS ST', type: 'number', required: false },
        { name: 'ipiAmount', example: 0, label: 'IPI', type: 'number', required: false },
        { name: 'pisAmount', example: 160.46, label: 'PIS', type: 'number', required: false },
        { name: 'cofinsAmount', example: 739.1, label: 'COFINS', type: 'number', required: false },
        { name: 'issAmount', example: 0, label: 'ISS', type: 'number', required: false },
        {
            name: 'totalAmount',
            example: 9725,
            label: 'Valor total',
            type: 'number',
            required: true,
            description: 'Fiscal total (defaults to productsAmount when no extras)',
        },
        {
            name: 'itemCount',
            example: 1,
            label: 'Itens',
            type: 'number',
            required: false,
            description: 'Computed line count',
        },
        {
            name: 'status',
            example: 'authorized',
            label: 'Status',
            type: 'enum',
            required: true,
            enumValues: [...InvoiceStatusValues],
            enumLabels: INVOICE_STATUS_LABELS,
            description: 'draft → authorized | cancelled',
            defaultValue: 'draft',
        },
        {
            name: 'generatesStockEntry',
            label: 'Gera entrada de estoque',
            type: 'boolean',
            required: false,
            description: 'When true, authorizing an IN invoice posts stock entries (0111)',
            defaultValue: false,
        },
        { name: 'netWeight', example: 250, label: 'Peso líquido', type: 'number', required: false },
        { name: 'grossWeight', example: 257.6, label: 'Peso bruto', type: 'number', required: false },
        { name: 'volumeCount', example: 14, label: 'Volumes', type: 'number', required: false },
        {
            name: 'receivingCheck',
            label: 'Conferência',
            type: 'string',
            required: false,
            description: 'none | requested | checked (recebimento — 0033)',
            defaultValue: 'none',
        },
        { name: 'checkRequestedAt', label: 'Conferência solicitada em', type: 'date', required: false },
        { name: 'checkRequestedBy', label: 'Conferência solicitada por', type: 'string', required: false },
        { name: 'checkedAt', label: 'Conferida em', type: 'date', required: false },
        { name: 'checkedBy', label: 'Conferida por', type: 'string', required: false },
        { name: 'volumesGeneratedAt', label: 'Volumes gerados em', type: 'date', required: false },
        { name: 'labelsPrintedAt', label: 'Etiquetas impressas em', type: 'date', required: false },
        ...AUDIT_FIELD_DESCRIPTORS,
        VERSION_FIELD_DESCRIPTOR,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'partyId',
            targetEntity: 'Party',
            relationship: 'many-to-one',
            displayField: 'displayName',
            description: 'Customer (OUT) or supplier (IN)',
        },
        {
            fieldName: 'carrierId',
            targetEntity: 'Party',
            relationship: 'many-to-one',
            displayField: 'displayName',
            description: 'Carrier party (optional)',
        },
        {
            fieldName: 'salespersonId',
            targetEntity: 'Party',
            relationship: 'many-to-one',
            displayField: 'displayName',
            description: 'Salesperson party (optional)',
        },
    ],
};
//# sourceMappingURL=invoice.descriptor.js.map